import React, { useEffect, useMemo, useState } from 'react';
import ExportButton from '../components/ExportButton';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';
import {
  fetchAnalyticsOverview,
  fetchBalances,
  fetchExpenses
} from '../services/backend-expenses';
import { getUser } from '../services/auth';
import { useTheme } from '../theme/ThemeContext';
import {
  daysInMonth,
  getISTParts,
  mondayPad,
  monthEnd
} from '../utils/istDate';
import './Analytics.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

const inr = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const inr2 = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const inrShort = (n) => {
  const v = Number(n || 0);
  if (v >= 100000) return `₹${(v / 100000).toFixed(2)}L`;
  if (v >= 1000) return `₹${(v / 1000).toFixed(1)}k`;
  return inr(v);
};

const CHART_COLORS = ['#9184d9', '#6ea8fe', '#e6c07b', '#5ecf9a', '#f07178', '#7a8299', '#c4b5fd', '#38bdf8'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const RANGE_CHIPS = [
  ['7d', 'Last 7 days'],
  ['month', 'This month'],
  ['30d', 'Last 30 days'],
  ['90d', 'Last 3 months'],
  ['ytd', 'YTD'],
  ['2025', 'Year 2025'],
  ['2024', 'Year 2024']
];

function isSpendRow(e) {
  const t = e.transaction_type == null ? 'debit' : e.transaction_type;
  return t === 'debit';
}

function isIncomeRow(e) {
  return e.transaction_type === 'credit';
}

function heatLevel(value, max) {
  if (!value || max <= 0) return 0;
  const ratio = value / max;
  if (ratio > 0.75) return 4;
  if (ratio > 0.5) return 3;
  if (ratio > 0.25) return 2;
  return 1;
}

function readCssVar(name, fallback) {
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return v || fallback;
  } catch (_) {
    return fallback;
  }
}

const Analytics = () => {
  const user = getUser();
  const telegramId = user?.telegram_id;
  const { theme } = useTheme();
  const todayParts = useMemo(() => getISTParts(), []);

  const [tab, setTab] = useState('overview');
  const [preset, setPreset] = useState('month');
  const [calFilter, setCalFilter] = useState(null);
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState('');

  const [calMode, setCalMode] = useState('month');
  const [viewY, setViewY] = useState(todayParts.year);
  const [viewM, setViewM] = useState(todayParts.month - 1);
  const [yearViewY, setYearViewY] = useState(todayParts.year);
  const [calMonthOv, setCalMonthOv] = useState(null);
  const [calYearOv, setCalYearOv] = useState(null);
  const [moversExpanded, setMoversExpanded] = useState(false);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [category, setCategory] = useState('All');
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [bankFilter, setBankFilter] = useState('All');
  const [banks, setBanks] = useState(['All']);
  const [serverData, setServerData] = useState([]);
  const [txLoading, setTxLoading] = useState(false);

  useEffect(() => {
    if (!telegramId) return;
    let cancelled = false;
    (async () => {
      setOverviewLoading(true);
      setOverviewError('');
      try {
        const params = calFilter
          ? { startDate: calFilter.startDate, endDate: calFilter.endDate }
          : { preset };
        const ov = await fetchAnalyticsOverview(telegramId, params);
        if (!cancelled) setOverview(ov);
      } catch (e) {
        console.error(e);
        if (!cancelled) setOverviewError('Failed to load analytics overview');
      } finally {
        if (!cancelled) setOverviewLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [telegramId, preset, calFilter]);

  useEffect(() => {
    if (!telegramId || calMode !== 'month') return;
    let cancelled = false;
    (async () => {
      try {
        const start = `${viewY}-${String(viewM + 1).padStart(2, '0')}-01`;
        const end = monthEnd(viewY, viewM + 1);
        const ov = await fetchAnalyticsOverview(telegramId, { startDate: start, endDate: end });
        if (!cancelled) setCalMonthOv(ov);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => { cancelled = true; };
  }, [telegramId, calMode, viewY, viewM]);

  useEffect(() => {
    if (!telegramId || calMode !== 'year') return;
    let cancelled = false;
    (async () => {
      try {
        const start = `${yearViewY}-01-01`;
        const end = yearViewY === todayParts.year
          ? todayParts.dateStr
          : `${yearViewY}-12-31`;
        const ov = await fetchAnalyticsOverview(telegramId, { startDate: start, endDate: end });
        if (!cancelled) setCalYearOv(ov);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => { cancelled = true; };
  }, [telegramId, calMode, yearViewY, todayParts.year, todayParts.dateStr]);

  useEffect(() => {
    if (!telegramId || tab !== 'transactions') return;
    let cancelled = false;
    (async () => {
      setTxLoading(true);
      try {
        const [list, bals] = await Promise.all([
          fetchExpenses(telegramId),
          fetchBalances(telegramId)
        ]);
        if (cancelled) return;
        setServerData(list || []);
        setBanks(['All', ...((bals || []).map((b) => b.bank_name))]);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setTxLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [telegramId, tab]);

  const filteredTx = useMemo(() => {
    let list = serverData;
    if (startDate) list = list.filter((e) => e.transaction_date >= startDate);
    if (endDate) list = list.filter((e) => e.transaction_date <= endDate);
    if (category && category !== 'All') list = list.filter((e) => e.category_name === category);
    if (bankFilter && bankFilter !== 'All') {
      list = list.filter((e) => String(e.bank || '').toLowerCase() === String(bankFilter).toLowerCase());
    }
    if (typeFilter === 'Debit') list = list.filter((e) => isSpendRow(e));
    if (typeFilter === 'Credit') list = list.filter((e) => isIncomeRow(e));
    if (typeFilter === 'Exclude mandates') list = list.filter((e) => e.transaction_type !== 'mandate');
    if (typeFilter === 'Mandates') list = list.filter((e) => e.transaction_type === 'mandate');
    if (query) {
      const q = query.toLowerCase();
      list = list.filter((e) => (e.merchant || '').toLowerCase().includes(q));
    }
    return list;
  }, [serverData, startDate, endDate, category, bankFilter, typeFilter, query]);

  const txCategories = useMemo(() => {
    const set = new Set();
    serverData.forEach((e) => { if (e.category_name) set.add(e.category_name); });
    return ['All', ...Array.from(set).sort()];
  }, [serverData]);

  const matchingSpend = useMemo(
    () => filteredTx.filter(isSpendRow).reduce((s, e) => s + Number(e.amount || 0), 0),
    [filteredTx]
  );
  const matchingIncome = useMemo(
    () => filteredTx.filter(isIncomeRow).reduce((s, e) => s + Number(e.amount || 0), 0),
    [filteredTx]
  );

  const exportRows = filteredTx.map((e) => ({
    id: e.id,
    date: e.transaction_date,
    merchant: e.merchant,
    category: e.category_name || 'Uncategorized',
    type: e.transaction_type || 'debit',
    amount: Number(e.amount),
    bank: e.bank || ''
  }));

  const chartTheme = useMemo(() => {
    const muted = readCssVar('--pg-muted', theme === 'dark' ? 'rgba(233,233,237,0.55)' : '#6b7280');
    const text = readCssVar('--pg-text', theme === 'dark' ? '#e9e9ed' : '#1f2937');
    const line = readCssVar('--pg-line', theme === 'dark' ? 'rgba(233,233,237,0.08)' : 'rgba(0,0,0,0.08)');
    return { muted, text, line };
  }, [theme]);

  const kpis = overview?.kpis;
  const cashflowData = {
    labels: (overview?.monthly || []).map((m) => m.month),
    datasets: [
      {
        label: 'Income',
        data: (overview?.monthly || []).map((m) => m.income),
        backgroundColor: 'rgba(94,207,154,0.75)',
        borderRadius: 4
      },
      {
        label: 'Spend',
        data: (overview?.monthly || []).map((m) => m.spend),
        backgroundColor: 'rgba(145,132,217,0.85)',
        borderRadius: 4
      }
    ]
  };

  const catSlice = (overview?.categories || []).slice(0, 8);
  const doughnutData = {
    labels: catSlice.map((c) => c.category),
    datasets: [{
      data: catSlice.map((c) => c.total),
      backgroundColor: CHART_COLORS,
      borderWidth: 0
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { labels: { color: chartTheme.text } } },
    scales: {
      x: { ticks: { color: chartTheme.muted }, grid: { color: chartTheme.line } },
      y: {
        ticks: {
          color: chartTheme.muted,
          callback: (v) => `₹${(Number(v) / 1000).toFixed(0)}k`
        },
        grid: { color: chartTheme.line }
      }
    }
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false }
    }
  };

  const categories = overview?.categories || [];
  const maxCat = Math.max(1, ...categories.map((c) => c.total || 0));
  const moversVisible = moversExpanded ? categories : categories.slice(0, 5);

  const daySpendMap = useMemo(() => {
    const map = new Map();
    (calMonthOv?.dailySpend || []).forEach((d) => map.set(d.day, d.spend));
    return map;
  }, [calMonthOv]);

  const maxDaySpend = Math.max(1, ...(calMonthOv?.dailySpend || []).map((d) => d.spend || 0));

  const monthSpendByIndex = useMemo(() => {
    const arr = Array(12).fill(0);
    (calYearOv?.monthly || []).forEach((m) => {
      // m.month like "2026-09" or "Sep 2026"
      const s = String(m.month || '');
      let idx = -1;
      const iso = s.match(/(\d{4})-(\d{2})/);
      if (iso) idx = Number(iso[2]) - 1;
      else {
        const short = SHORT_MONTHS.findIndex((n) => s.toLowerCase().startsWith(n.toLowerCase()));
        if (short >= 0) idx = short;
      }
      if (idx >= 0) arr[idx] = Number(m.spend || 0);
    });
    return arr;
  }, [calYearOv]);

  const maxMonthSpend = Math.max(1, ...monthSpendByIndex);

  const canNextMonth = viewY < todayParts.year || (viewY === todayParts.year && viewM < todayParts.month - 1);
  const canNextYear = yearViewY < todayParts.year;
  const canPrevYear = yearViewY > 2021;

  const dayCells = useMemo(() => {
    const pad = mondayPad(viewY, viewM);
    const dim = daysInMonth(viewY, viewM + 1);
    const cells = [];
    for (let i = 0; i < pad; i++) cells.push({ empty: true, key: `e${i}` });
    const isCurrent = viewY === todayParts.year && viewM === todayParts.month - 1;
    for (let d = 1; d <= dim; d++) {
      const iso = `${viewY}-${String(viewM + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const future = isCurrent && d > todayParts.day;
      const spend = daySpendMap.get(iso) || 0;
      cells.push({
        empty: false,
        key: iso,
        day: d,
        iso,
        future,
        spend,
        level: heatLevel(spend, maxDaySpend),
        selected: calFilter?.kind === 'day' && calFilter.startDate === iso
      });
    }
    return cells;
  }, [viewY, viewM, todayParts, daySpendMap, maxDaySpend, calFilter]);

  const selectDay = (iso) => {
    setCalFilter({
      kind: 'day',
      label: iso,
      startDate: iso,
      endDate: iso
    });
  };

  const selectMonth = (month1to12) => {
    const start = `${yearViewY}-${String(month1to12).padStart(2, '0')}-01`;
    const end = monthEnd(yearViewY, month1to12);
    const cappedEnd = yearViewY === todayParts.year && month1to12 === todayParts.month
      ? todayParts.dateStr
      : end;
    setCalFilter({
      kind: 'month',
      label: `${SHORT_MONTHS[month1to12 - 1]} ${yearViewY}`,
      startDate: start,
      endDate: cappedEnd
    });
  };

  const clearCalFilter = () => setCalFilter(null);

  const goPrevMonth = () => {
    setViewM((m) => {
      if (m === 0) {
        setViewY((y) => y - 1);
        return 11;
      }
      return m - 1;
    });
  };

  const goNextMonth = () => {
    if (!canNextMonth) return;
    setViewM((m) => {
      if (m === 11) {
        setViewY((y) => y + 1);
        return 0;
      }
      return m + 1;
    });
  };

  const onPreset = (key) => {
    setPreset(key);
    setCalFilter(null);
  };

  const onYearPick = (y) => {
    if (!y) return;
    setPreset(y);
    setCalFilter(null);
  };

  const spendDeltaClass = (kpis?.spendChangePct || 0) <= 0 ? 'good' : 'bad';
  const rangeNote = calFilter
    ? `${calFilter.startDate} → ${calFilter.endDate} · calendar ${calFilter.kind} filter`
    : overview?.range
      ? `${overview.range.startDate} → ${overview.range.endDate} · spend = debits only`
      : 'Loading range…';

  return (
    <div className="analytics-page">
      <p className="an-eyebrow">PocketGuru · analytics</p>
      <h1 className="an-title">Analytics</h1>
      <p className="an-lead">
        Overview separates spend from income. Calendar clicks filter charts on this page.
      </p>

      <div className="an-tabs" role="tablist">
        <button
          type="button"
          className={`an-tab ${tab === 'overview' ? 'on' : ''}`}
          onClick={() => setTab('overview')}
        >
          Overview
        </button>
        <button
          type="button"
          className={`an-tab ${tab === 'transactions' ? 'on' : ''}`}
          onClick={() => setTab('transactions')}
        >
          Transactions
        </button>
      </div>

      {tab === 'overview' && (
        <div>
          <div className="an-toolbar">
            {RANGE_CHIPS.map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={`an-chip ${!calFilter && preset === key ? 'on' : ''}`}
                onClick={() => onPreset(key)}
              >
                {label}
              </button>
            ))}
            <select
              className="an-field"
              value=""
              onChange={(e) => onYearPick(e.target.value)}
              title="Prior years"
            >
              <option value="">More years…</option>
              <option value="2023">2023</option>
              <option value="2022">2022</option>
              <option value="2021">2021</option>
            </select>
            <span className="an-spacer" />
            <span className="an-note">{rangeNote}</span>
          </div>

          {overviewError && <p className="an-error">{overviewError}</p>}
          {overviewLoading && !overview && <p className="an-note">Loading overview…</p>}

          {kpis && (
            <div className="an-kpis">
              <div className="an-kpi">
                <div className="label">Spend</div>
                <div className="value">{inr(kpis.spend)}</div>
                <div className={`delta ${spendDeltaClass}`}>
                  {kpis.spendChangePct <= 0 ? '↓' : '↑'}{' '}
                  {Math.abs(kpis.spendChangePct).toFixed(1)}% vs prior period
                </div>
              </div>
              <div className="an-kpi">
                <div className="label">Income</div>
                <div className="value">{inr(kpis.income)}</div>
                <div className="delta">{kpis.incomeCount} credits</div>
              </div>
              <div className="an-kpi">
                <div className="label">Net cash flow</div>
                <div className="value" style={{ color: kpis.netCashflow >= 0 ? 'var(--an-good)' : 'var(--an-bad)' }}>
                  {kpis.netCashflow >= 0 ? '+' : ''}{inr(kpis.netCashflow)}
                </div>
                <div className="delta">income − spend</div>
              </div>
              <div className="an-kpi">
                <div className="label">Savings rate</div>
                <div className="value">
                  {kpis.savingsRate == null ? '—' : `${kpis.savingsRate.toFixed(0)}%`}
                </div>
                <div className="delta">(income − spend) ÷ income</div>
              </div>
              <div className="an-kpi">
                <div className="label">Pace</div>
                <div className="value">
                  {inr(kpis.pacePerDay)}
                  <span style={{ fontSize: 14, color: 'var(--an-muted)' }}>/day</span>
                </div>
                <div className="delta">
                  {kpis.daysLeft} days left · proj {inr(kpis.projectedMonthSpend)}
                </div>
              </div>
            </div>
          )}

          <div className="an-grid">
            <div className="an-card">
              <h2>Income vs spend</h2>
              <p className="sub">Credits vs debits by month</p>
              <div className="an-chart">
                <Bar data={cashflowData} options={chartOptions} />
              </div>
            </div>
            <div className="an-card">
              <h2>Where spend went</h2>
              <p className="sub">Expense categories · legend beside chart</p>
              {catSlice.length === 0 ? (
                <p className="an-note">No debit spend in this range.</p>
              ) : (
                <div className="an-doughnut-wrap">
                  <div className="an-chart an-chart-doughnut">
                    <Doughnut data={doughnutData} options={doughnutOptions} />
                  </div>
                  <ul className="an-legend-list">
                    {catSlice.map((c, i) => (
                      <li key={c.category}>
                        <i style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                        {c.category}
                        <span className="m">{inr(c.total)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          <div className="an-grid3">
            <div className="an-card">
              <h2>Top merchants</h2>
              <p className="sub">By debit spend</p>
              <ul className="an-list">
                {(overview?.topMerchants || []).length === 0 && (
                  <li><span className="m">No merchants yet</span></li>
                )}
                {(overview?.topMerchants || []).map((m) => (
                  <li key={m.merchant}>
                    <div>
                      {m.merchant}
                      <span className="m"><br />{m.txnCount} txns{m.category ? ` · ${m.category}` : ''}</span>
                    </div>
                    <div>{inr(m.total)}</div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="an-card">
              <h2>Category movers</h2>
              <p className="sub">Spend by category in range · bars = share of top</p>
              <div className={`an-movers-wrap ${moversExpanded ? 'expanded' : ''}`}>
                {moversVisible.length === 0 && <p className="an-note">No categories yet</p>}
                {moversVisible.map((c) => {
                  const pct = Math.round((c.total / maxCat) * 100);
                  const mover = (overview?.movers || []).find((m) => m.category === c.category);
                  const delta = mover?.delta;
                  const fillClass = delta == null ? '' : delta > 0 ? 'up' : delta < 0 ? 'down' : '';
                  return (
                    <div className="an-bar-row" key={c.category}>
                      <span>{c.emoji ? `${c.emoji} ` : ''}{c.category}</span>
                      <div className="track"><div className={`fill ${fillClass}`} style={{ width: `${pct}%` }} /></div>
                      <span className={`delta ${delta > 0 ? 'bad' : delta < 0 ? 'good' : ''}`}>
                        {inrShort(c.total)}
                      </span>
                    </div>
                  );
                })}
              </div>
              {categories.length > 5 && (
                <div className="an-movers-footer">
                  <button type="button" onClick={() => setMoversExpanded((v) => !v)}>
                    {moversExpanded ? 'Show less' : 'Show all categories'}
                  </button>
                </div>
              )}
            </div>

            <div className={`an-card an-spend-cal mode-${calMode}`}>
              <h2>Spend calendar</h2>
              <p className="sub">
                {calMode === 'year'
                  ? 'Monthly debit intensity · click a square to filter charts above'
                  : 'Daily debit intensity · click a day to filter charts above'}
              </p>
              <div className="an-cal-toolbar">
                <button
                  type="button"
                  className={`an-chip ${calMode === 'month' ? 'on' : ''}`}
                  onClick={() => { setCalMode('month'); clearCalFilter(); }}
                >
                  Month · days
                </button>
                <button
                  type="button"
                  className={`an-chip ${calMode === 'year' ? 'on' : ''}`}
                  onClick={() => { setCalMode('year'); clearCalFilter(); }}
                >
                  Year · months
                </button>
              </div>

              {calMode === 'month' && (
                <div className="an-cal-nav">
                  <button type="button" onClick={goPrevMonth} aria-label="Previous month">‹</button>
                  <span className="title">{MONTH_NAMES[viewM]} {viewY}</span>
                  <button type="button" onClick={goNextMonth} disabled={!canNextMonth} aria-label="Next month">›</button>
                </div>
              )}
              {calMode === 'year' && (
                <div className="an-cal-nav">
                  <button
                    type="button"
                    onClick={() => canPrevYear && setYearViewY((y) => y - 1)}
                    disabled={!canPrevYear}
                    aria-label="Previous year"
                  >
                    ‹
                  </button>
                  <span className="title">{yearViewY}</span>
                  <button
                    type="button"
                    onClick={() => canNextYear && setYearViewY((y) => y + 1)}
                    disabled={!canNextYear}
                    aria-label="Next year"
                  >
                    ›
                  </button>
                </div>
              )}

              <p className="an-cal-hint">
                <strong>Month · days:</strong> one square = one day.{' '}
                <strong>Year · months:</strong> one square = one month (labels above/below). Click filters charts here.
              </p>

              {calMode === 'month' && (
                <div>
                  <div className="an-cal-weekdays">
                    {WEEKDAYS.map((w) => <span key={w}>{w}</span>)}
                  </div>
                  <div className="an-heat">
                    {dayCells.map((c) => (
                      c.empty || c.future ? (
                        <button key={c.key} type="button" className="empty" disabled aria-hidden="true" />
                      ) : (
                        <button
                          key={c.key}
                          type="button"
                          className={`${c.level ? `l${c.level}` : ''} ${c.selected ? 'on' : ''}`}
                          title={`${c.iso}: ${inr2(c.spend)}`}
                          onClick={() => selectDay(c.iso)}
                        />
                      )
                    ))}
                  </div>
                </div>
              )}

              {calMode === 'year' && (
                <div>
                  <div className="an-month-labels" aria-hidden="true">
                    {SHORT_MONTHS.slice(0, 6).map((n) => <span key={n}>{n}</span>)}
                  </div>
                  <div className="an-heat-months">
                    {SHORT_MONTHS.slice(0, 6).map((n, i) => {
                      const spend = monthSpendByIndex[i];
                      const future = yearViewY === todayParts.year && i > todayParts.month - 1;
                      const level = heatLevel(spend, maxMonthSpend);
                      const selected = calFilter?.kind === 'month' && calFilter.label === `${n} ${yearViewY}`;
                      return (
                        <button
                          key={n}
                          type="button"
                          className={`${level ? `l${level}` : ''} ${selected ? 'on' : ''}`}
                          disabled={future || !spend}
                          title={`${n} ${yearViewY}${spend ? ` · ${inrShort(spend)}` : ''}`}
                          aria-label={`${n} ${yearViewY}`}
                          onClick={() => selectMonth(i + 1)}
                        />
                      );
                    })}
                  </div>
                  <div className="an-heat-months" style={{ marginTop: 6 }}>
                    {SHORT_MONTHS.slice(6).map((n, j) => {
                      const i = j + 6;
                      const spend = monthSpendByIndex[i];
                      const future = yearViewY === todayParts.year && i > todayParts.month - 1;
                      const level = heatLevel(spend, maxMonthSpend);
                      const selected = calFilter?.kind === 'month' && calFilter.label === `${n} ${yearViewY}`;
                      return (
                        <button
                          key={n}
                          type="button"
                          className={`${level ? `l${level}` : ''} ${selected ? 'on' : ''}`}
                          disabled={future || !spend}
                          title={`${n} ${yearViewY}${spend ? ` · ${inrShort(spend)}` : ''}`}
                          aria-label={`${n} ${yearViewY}`}
                          onClick={() => selectMonth(i + 1)}
                        />
                      );
                    })}
                  </div>
                  <div className="an-month-labels bottom" aria-hidden="true">
                    {SHORT_MONTHS.slice(6).map((n) => <span key={n}>{n}</span>)}
                  </div>
                </div>
              )}

              <div className="an-heat-legend">
                <span>Less</span>
                <i className="l0" />
                <i className="l2" />
                <i className="l4" />
                <span>More</span>
              </div>

              {calFilter && (
                <div className="an-filter-chip">
                  Charts filtered: <strong>{calFilter.label}</strong>
                  <button type="button" onClick={clearCalFilter} aria-label="Clear">×</button>
                </div>
              )}
            </div>
          </div>

          <div className="an-card" style={{ marginTop: 14 }}>
            <h2>Money map</h2>
            <p className="sub">Simple flow for this range (Sankey later)</p>
            <ul className="an-list">
              <li><div>Income in</div><div style={{ color: 'var(--an-good)' }}>{inr(kpis?.income)}</div></li>
              <li><div>→ Living spend</div><div>{inr(kpis?.spend)}</div></li>
              <li>
                <div>Left after spend</div>
                <div style={{ color: 'var(--an-accent-soft)' }}>{inr(kpis?.netCashflow)}</div>
              </li>
            </ul>
          </div>
        </div>
      )}

      {tab === 'transactions' && (
        <div>
          <div className="an-toolbar an-toolbar-wrap">
            <input className="an-field" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} title="Start" />
            <input className="an-field" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} title="End" />
            <select className="an-field" value={category} onChange={(e) => setCategory(e.target.value)}>
              {txCategories.map((c) => <option key={c} value={c}>{c === 'All' ? 'Category: All' : c}</option>)}
            </select>
            <select className="an-field" value={bankFilter} onChange={(e) => setBankFilter(e.target.value)}>
              {banks.map((b) => <option key={b} value={b}>{b === 'All' ? 'Bank: All' : b}</option>)}
            </select>
            <select className="an-field" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option>All</option>
              <option>Debit</option>
              <option>Credit</option>
              <option>Exclude mandates</option>
              <option>Mandates</option>
            </select>
            <input
              className="an-field an-search"
              type="search"
              placeholder="Search merchant"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <span className="an-spacer" />
            <ExportButton data={exportRows} />
          </div>

          <div className="an-tx-summary">
            <div className="an-card">
              <div className="label">Matching spend</div>
              <div className="value" style={{ color: 'var(--an-bad)' }}>{inr2(matchingSpend)}</div>
              <p className="sub">Debits in filter · credits excluded</p>
            </div>
            <div className="an-card">
              <div className="label">Matching income</div>
              <div className="value" style={{ color: 'var(--an-good)' }}>{inr2(matchingIncome)}</div>
              <p className="sub">Credits in filter</p>
            </div>
            <div className="an-card">
              <div className="label">Rows shown</div>
              <div className="value">{filteredTx.length}</div>
              <p className="sub">{txLoading ? 'Loading…' : 'Current filters'}</p>
            </div>
          </div>

          <div className="an-card an-table-card">
            <div className="table-wrapper">
              <table className="an-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Merchant</th>
                    <th>Category</th>
                    <th>Bank</th>
                    <th>Type</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTx.length === 0 && (
                    <tr><td colSpan={6} className="an-note">No transactions match.</td></tr>
                  )}
                  {filteredTx.map((e) => {
                    const credit = isIncomeRow(e);
                    const mandate = e.transaction_type === 'mandate';
                    return (
                      <tr key={e.id}>
                        <td>{e.transaction_date}</td>
                        <td>{e.merchant}</td>
                        <td>{e.category_name || 'Uncategorized'}</td>
                        <td>{e.bank || '—'}</td>
                        <td>{e.transaction_type || 'debit'}</td>
                        <td
                          style={{
                            textAlign: 'right',
                            color: mandate ? '#ca8a04' : credit ? 'var(--an-good)' : 'var(--an-bad)'
                          }}
                        >
                          {credit ? '+' : mandate ? '' : '−'}
                          {inr2(e.amount)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;
